/*
 * Copyright © 2025 AionTrack. All rights reserved.
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import posthog from 'posthog-js'
import { initPostHog, isPostHogReady, getPostHogConfig } from '@/lib/analytics/posthog'
import { AnalyticsEvents, type AnalyticsEventName } from '@/lib/analytics/events'
import type { PostHogUserProperties, AnalyticsEventProperties } from '@/types/analytics'

/**
 * PostHog context interface
 */
interface PostHogContextType {
  /** Whether PostHog is initialized and ready */
  isReady: boolean
  /** Whether analytics is enabled */
  isEnabled: boolean
  /** Track a custom event */
  track: (event: AnalyticsEventName | string, properties?: AnalyticsEventProperties) => void
  /** Identify the current user */
  identify: (userId: string, properties?: PostHogUserProperties) => void
  /** Reset user identification (on logout) */
  reset: () => void
  /** Set user properties without identifying */
  setUserProperties: (properties: Partial<PostHogUserProperties>) => void
  /** Track a page view manually */
  trackPageView: (pageName?: string, properties?: Record<string, any>) => void
}

const PostHogContext = createContext<PostHogContextType | undefined>(undefined)

/**
 * Hook to access PostHog analytics context
 */
export function usePostHog() {
  const context = useContext(PostHogContext)
  if (context === undefined) {
    throw new Error('usePostHog must be used within a PostHogProvider')
  }
  return context
}

interface PostHogProviderProps {
  children: React.ReactNode
}

/**
 * PostHog analytics provider component
 * Initializes PostHog and provides analytics methods to children
 */
export function PostHogProvider({ children }: PostHogProviderProps) {
  const [isReady, setIsReady] = useState(false)
  const [isEnabled, setIsEnabled] = useState(false)
  const location = useLocation()
  const previousPathRef = useRef<string>('')
  const lastIdentifiedUserRef = useRef<string | null>(null)

  // Initialize PostHog on mount
  useEffect(() => {
    const config = getPostHogConfig()
    setIsEnabled(config.enabled)

    if (config.enabled) {
      const initialized = initPostHog()
      setIsReady(initialized)
    }

    // Cleanup on unmount
    return () => {
      if (isPostHogReady()) {
        posthog.capture('$pageleave')
      }
    }
  }, [])

  // Track page views on route changes
  useEffect(() => {
    if (!isReady) return

    const currentPath = location.pathname + location.search

    // Avoid duplicate tracking
    if (currentPath === previousPathRef.current) return
    previousPathRef.current = currentPath

    // Redact sensitive tokens from URLs before sending to analytics
    const redactedPath = location.pathname.replace(
      /\/invitations\/[^/]+/,
      '/invitations/[REDACTED]'
    )
    const redactedUrl = window.location.href.replace(
      /\/invitations\/[^/?#]+/,
      '/invitations/[REDACTED]'
    )

    // Track page view with route information
    posthog.capture(AnalyticsEvents.PAGE_VIEW, {
      $current_url: redactedUrl,
      path: redactedPath,
      search: location.search,
      referrer: document.referrer,
      // Extract page name from path
      page_name: getPageNameFromPath(location.pathname),
    })
  }, [location, isReady])

  /**
   * Track a custom event
   */
  const track = useCallback((
    event: AnalyticsEventName | string,
    properties?: AnalyticsEventProperties
  ) => {
    if (!isReady) {
      if (import.meta.env.DEV) {
        console.log('[PostHog] Would track (not ready):', event, properties)
      }
      return
    }

    posthog.capture(event, {
      ...properties,
      timestamp: new Date().toISOString(),
      source: 'web',
    })
  }, [isReady])

  /**
   * Identify the current user
   */
  const identify = useCallback((
    userId: string,
    properties?: PostHogUserProperties
  ) => {
    if (!isReady) {
      if (import.meta.env.DEV) {
        console.log('[PostHog] Would identify (not ready):', userId, properties)
      }
      return
    }

    // Skip if we already identified this user (silently prevent duplicates)
    if (lastIdentifiedUserRef.current === userId) {
      return
    }

    posthog.identify(userId, {
      ...properties,
      $set_once: {
        first_seen: new Date().toISOString(),
      },
    })
    lastIdentifiedUserRef.current = userId
  }, [isReady])

  /**
   * Reset user identification (call on logout)
   */
  const reset = useCallback(() => {
    if (!isReady) return
    posthog.reset()
    lastIdentifiedUserRef.current = null
  }, [isReady])

  /**
   * Set user properties without identifying
   */
  const setUserProperties = useCallback((
    properties: Partial<PostHogUserProperties>
  ) => {
    if (!isReady) return
    posthog.people.set(properties)
  }, [isReady])

  /**
   * Track a page view manually
   */
  const trackPageView = useCallback((
    pageName?: string,
    properties?: Record<string, any>
  ) => {
    if (!isReady) return

    posthog.capture(AnalyticsEvents.PAGE_VIEW, {
      $current_url: window.location.href.replace(/\/invitations\/[^/?#]+/, '/invitations/[REDACTED]'),
      path: location.pathname.replace(/\/invitations\/[^/]+/, '/invitations/[REDACTED]'),
      page_name: pageName || getPageNameFromPath(location.pathname),
      ...properties,
    })
  }, [isReady, location.pathname])

  const value: PostHogContextType = {
    isReady,
    isEnabled,
    track,
    identify,
    reset,
    setUserProperties,
    trackPageView,
  }

  return (
    <PostHogContext.Provider value={value}>
      {children}
    </PostHogContext.Provider>
  )
}

/**
 * Extract a readable page name from the route path
 */
function getPageNameFromPath(path: string): string {
  // Remove leading slash and split
  const segments = path.replace(/^\//, '').split('/')

  // Handle known route patterns
  if (segments[0] === 'projects' && segments.length >= 3) {
    // /projects/:id/dashboard -> "Project Dashboard"
    // /projects/:id/clips -> "Project Clips"
    const section = segments[2] || 'dashboard'
    return `Project ${capitalize(section.replace(/-/g, ' '))}`
  }

  if (segments[0] === 'projects') {
    return 'Projects List'
  }

  // Auth pages
  const authPages: Record<string, string> = {
    'login': 'Login',
    'signup': 'Sign Up',
    'forgot-password': 'Forgot Password',
    'reset-password': 'Reset Password',
  }

  if (authPages[segments[0]]) {
    return authPages[segments[0]]
  }

  // Admin pages
  if (segments[0] === 'admin') {
    return 'Admin Dashboard'
  }

  // Default: capitalize the first segment
  return capitalize(segments[0] || 'home')
}

function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1)
}
